import { useQuery } from "@apollo/client";
import { useEffect } from "react";
import {
  ACTIVE_CALL_TRANSCRIPT_STATUS_QUERY,
  type ActiveCallTranscriptRunnerUp,
  type ActiveCallTranscriptStatusResponse,
  type TranscriptSummaryStatus
} from "./queries";
import { shouldPollActiveCallTranscript } from "./transcriptPollUtils";

export { isTranscriptSummaryTerminal, shouldPollActiveCallTranscript } from "./transcriptPollUtils";

export const ACTIVE_CALL_TRANSCRIPT_POLL_INTERVAL_MS = 5000;

export type UseActiveCallTranscriptPollOptions = {
  enabled: boolean;
  callEnded: boolean;
  callSid?: string | null;
};

export type UseActiveCallTranscriptPollResult = {
  callSid: string | null;
  endedAt: string | null;
  transcriptSummaryStatus: TranscriptSummaryStatus | null;
  transcriptSummary: string | null;
  transcriptTopic: string | null;
  transcriptSubtopic: string | null;
  runnersUp: ActiveCallTranscriptRunnerUp[];
  interactionId: string | null;
  loading: boolean;
  isPolling: boolean;
};

export default function useActiveCallTranscriptPoll({
  enabled,
  callEnded,
  callSid
}: UseActiveCallTranscriptPollOptions): UseActiveCallTranscriptPollResult {
  const shouldQuery = enabled && callEnded && !!callSid;

  const { data, loading, error, startPolling, stopPolling } = useQuery<ActiveCallTranscriptStatusResponse>(
    ACTIVE_CALL_TRANSCRIPT_STATUS_QUERY,
    {
      variables: { callSid: callSid || "" },
      skip: !shouldQuery,
      fetchPolicy: "network-only"
    }
  );

  const response = data?.twilioFlexCall;
  const responseMatchesSelection = !!callSid && response?.callSid === callSid;
  // A query error (auth, deploy skew, network) is terminal for this sheet: report
  // failed so wrap-up requires notes and stops showing the generating state.
  const transcriptSummaryStatus = error ? "failed" : responseMatchesSelection ? response.transcriptSummaryStatus : null;
  const isPolling =
    !error &&
    shouldPollActiveCallTranscript({
      enabled,
      callEnded,
      callSid,
      transcriptSummaryStatus
    });

  useEffect(() => {
    // A query error (auth, deploy skew, network) would otherwise refetch in a
    // hot loop: data stays undefined, status stays null, and polling never stops.
    if (!shouldQuery || error) {
      stopPolling();
      return;
    }

    if (isPolling) {
      startPolling(ACTIVE_CALL_TRANSCRIPT_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [error, isPolling, shouldQuery, startPolling, stopPolling]);

  return {
    callSid: responseMatchesSelection ? response.callSid : null,
    endedAt: responseMatchesSelection ? response.endedAt : null,
    transcriptSummaryStatus,
    transcriptSummary: responseMatchesSelection ? response.transcriptSummary : null,
    transcriptTopic: responseMatchesSelection ? response.transcriptTopic : null,
    transcriptSubtopic: responseMatchesSelection ? response.transcriptSubtopic : null,
    runnersUp: responseMatchesSelection ? (response.runnersUp ?? []) : [],
    interactionId: responseMatchesSelection ? response.interactionId : null,
    loading: shouldQuery && loading,
    isPolling
  };
}
